import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/base/ComicCard";
import useSeo from "@/hooks/useSeo";
import { categories, comics } from "@/mocks/manga";

const sortOptions = [
  { id: "popular", label: "按热度" },
  { id: "rating", label: "按评分" },
  { id: "newest", label: "按更新" },
];

const categorySchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: comics.slice(0, 12).map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.title,
    url: `/comic/${c.id}`,
  })),
};

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const keyword = searchParams.get("q") || "";
  const [sort, setSort] = useState("popular");

  const currentName = categories.find((c) => c.id === activeCat)?.name || "全部";

  const filtered = useMemo(() => {
    let list = comics;
    if (activeCat !== "all") {
      list = list.filter((c) => c.category === activeCat);
    }
    if (keyword) {
      const kw = keyword.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.description.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.includes(keyword))
      );
    }
    if (sort === "rating") return [...list].sort((a, b) => b.rating - a.rating);
    if (sort === "newest") return [...list].sort((a, b) => b.popularity - a.popularity);
    return [...list].sort((a, b) => b.popularity - a.popularity);
  }, [activeCat, keyword, sort]);

  const setCat = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("cat");
    else next.set("cat", id);
    setSearchParams(next);
  };

  useSeo({
    title: `${keyword ? `"${keyword}" 的搜索结果` : `${currentName}漫画`} - 在线漫画分类`,
    description:
      keyword
        ? `漫悦漫画网搜索"${keyword}"的结果，翻页模式、横屏阅读、夜间模式与缓存下载全支持，找到你爱的漫画。`
        : `浏览${currentName}漫画分类，海量优质${currentName}题材作品，支持翻页模式、横屏阅读、夜间模式与缓存下载。`,
    keywords: `${currentName}漫画,漫画分类,${currentName}漫画推荐`,
    path: "/categories",
    jsonLd: [categorySchema],
  });

  return (
    <Layout>
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <nav className="text-xs text-foreground-500" aria-label="面包屑导航">
            <ol className="flex flex-wrap items-center gap-1">
              <li><a href="/" className="hover:text-primary-600">首页</a></li>
              <li><i className="ri-arrow-right-s-line" aria-hidden="true" /></li>
              <li className="text-foreground-950">{currentName}漫画</li>
            </ol>
          </nav>
          <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
            {keyword ? `搜索"${keyword}"` : `${currentName}漫画`}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword
              ? `找到 ${filtered.length} 部相关漫画作品`
              : `${currentName}题材精选，翻页流畅、画质清晰，支持夜间模式与缓存下载。`}
          </p>
        </div>
      </section>

      {/* 分类切换 */}
      <section className="bg-background-50 py-6">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === c.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              {sortOptions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSort(s.id)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === s.id
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="hidden text-xs text-foreground-500 sm:block">共 {filtered.length} 部作品</p>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {filtered.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center py-10 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-300" aria-hidden="true" />
              <p className="mt-4 text-foreground-600">未找到相关漫画，换个关键词试试吧</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}